import React, { FC, useEffect, useState, useCallback, useRef } from 'react';
import {
  Dialog,
  IconButton,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { ProjectMedia } from 'src/api/Api';
import clientService from 'src/clientService';

interface MediaViewerDialogProps {
  open: boolean;
  onClose: () => void;
  mediaList: ProjectMedia[];
  initialIndex: number;
}

const MediaViewerDialog: FC<MediaViewerDialogProps> = ({ open, onClose, mediaList, initialIndex }) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentMedia = mediaList[currentIndex];

  useEffect(() => {
    if (open) {
      setCurrentIndex(initialIndex);
    }
  }, [open, initialIndex]);

  useEffect(() => {
    const fetchDownloadUrl = async () => {
      if (!currentMedia?.media_id) return;
      
      setLoading(true);
      try {
        const response = await clientService.api.getProjectMediaDownloadUrlApiV1AdminProjectMediaMediaIdDownloadUrlGet(currentMedia.media_id);
        setDownloadUrl(response.data.download_url);
      } catch (err) {
        console.error('Failed to fetch download URL', err);
        setDownloadUrl(null);
      } finally {
        setLoading(false);
      }
    };

    if (open) {
      fetchDownloadUrl();
    }
  }, [currentMedia?.media_id, open]);

  const handleNext = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentIndex < mediaList.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0); // Loop back to start
    }
  }, [currentIndex, mediaList.length]);

  const handlePrev = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(mediaList.length - 1); // Loop to end
    }
  }, [currentIndex, mediaList.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleNext, handlePrev, onClose]);

  if (!open) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'transparent',
          boxShadow: 'none',
          overflow: 'visible',
          m: 0,
          maxHeight: '100vh',
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }
      }}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
        }
      }}
      onClick={onClose}
    >
      <Box 
        sx={{ 
          position: 'relative', 
          width: '100%', 
          height: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 4
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking content
      >
        {/* Close Button - positioned relative to viewport/container */}
        <IconButton 
          onClick={onClose}
          sx={{ 
            position: 'absolute', 
            top: 20, 
            right: 20, 
            color: 'white',
            bgcolor: 'rgba(0,0,0,0.5)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            zIndex: 10
          }}
        >
          <CloseIcon fontSize="large" />
        </IconButton>

        {/* Previous Button */}
        {mediaList.length > 1 && (
          <IconButton 
            onClick={handlePrev}
            sx={{ 
              position: 'absolute', 
              left: 20, 
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              zIndex: 10
            }}
          >
            <ArrowBackIosNewIcon fontSize="large" />
          </IconButton>
        )}

        {/* Media Content */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          width: '100%',
          height: '100%',
        }}>
          {loading ? (
            <CircularProgress color="inherit" />
          ) : downloadUrl ? (
            <>
              {currentMedia.media_type === 'IMAGE' && (
                <img 
                  src={downloadUrl} 
                  alt={currentMedia.caption || ''} 
                  style={{ 
                    maxWidth: '90%', 
                    maxHeight: '85vh', 
                    objectFit: 'contain',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    borderRadius: '4px'
                  }} 
                />
              )}
              {currentMedia.media_type === 'VIDEO' && (
                <video 
                  ref={videoRef}
                  src={downloadUrl} 
                  controls 
                  autoPlay
                  style={{ 
                    maxWidth: '90%', 
                    maxHeight: '85vh',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    borderRadius: '4px'
                  }} 
                />
              )}
              {currentMedia.media_type === 'AUDIO' && (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    bgcolor: 'rgba(255,255,255,0.05)',
                    p: 6,
                    borderRadius: 4,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                    width: '100%',
                    maxWidth: '400px'
                  }}
                >
                  <MusicNoteIcon sx={{ fontSize: 100, color: 'white', mb: 4, opacity: 0.8 }} />
                  <audio 
                    src={downloadUrl} 
                    controls 
                    autoPlay
                    style={{ width: '100%' }} 
                  />
                </Box>
              )}
              {currentMedia.caption && (
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mt: 2, 
                    color: 'white', 
                    textAlign: 'center',
                    textShadow: '0 2px 4px rgba(0,0,0,0.8)',
                    maxWidth: '80%'
                  }}
                >
                  {currentMedia.caption}
                </Typography>
              )}
            </>
          ) : (
            <Typography color="error">Failed to load media preview</Typography>
          )}
        </Box>

        {/* Next Button */}
        {mediaList.length > 1 && (
          <IconButton 
            onClick={handleNext}
            sx={{ 
              position: 'absolute', 
              right: 20, 
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.3)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              zIndex: 10
            }}
          >
            <ArrowForwardIosIcon fontSize="large" />
          </IconButton>
        )}
        
        {/* Pagination indicator */}
        <Box sx={{ 
          position: 'absolute', 
          bottom: 20, 
          color: 'rgba(255,255,255,0.7)',
          bgcolor: 'rgba(0,0,0,0.5)',
          px: 2,
          py: 0.5,
          borderRadius: 4
        }}>
          <Typography variant="caption">
            {currentIndex + 1} / {mediaList.length}
          </Typography>
        </Box>
      </Box>
    </Dialog>
  );
};

export default MediaViewerDialog;
